import {
  ActivityLine, AmortizationLine, CalculatorInputs, CalculationOutput, IRPFDetail,
  PaymentsDetail, RegionKey, RulesPack, VATDetail, TimelineCashMonth, QuarterDistribution, QuarterRow
} from "../types";
import { validateInputs } from "./validators";
import { calcIRPF, RegionRules } from "../lib/tax/irpf";
import { getTarifaPlanaDetails, suggestRetaAnnualFromIncome } from "./reta";

function clamp01(x: number) { return Math.max(0, Math.min(1, x)); }
function sum(nums: number[]) { return nums.reduce((a, b) => a + b, 0); }

function toAnnual(amount: number, freq?: string): number {
  return freq === 'monthly' ? amount * 12 : amount;
}

function vatNet(ivaRepercutido: number, ivaSoportadoDeducible: number) {
  return ivaRepercutido - ivaSoportadoDeducible;
}

function ratioIngresosConRetencion(activities: ActivityLine[]) {
  const total = activities.reduce((s,a)=>s+toAnnual(a.baseAmount, a.frequency),0) || 1;
  const conRet = activities
    .filter(a => a.retenType !== "none")
    .reduce((s,a)=>s+toAnnual(a.baseAmount, a.frequency),0);
  return conRet / total;
}

function totalCostsBaseCash(
  costsBase: number,
  housingExtra: number,
  vehicleExtra: number,
  healthInsuranceAnnualFull: number
) {
  return costsBase + housingExtra + vehicleExtra + (healthInsuranceAnnualFull || 0);
}

function normQuarterDist(d?: QuarterDistribution): QuarterDistribution {
  if (!d) return { income: [0.25, 0.25, 0.25, 0.25], costs: [0.25, 0.25, 0.25, 0.25] };
  return d;
}

function buildQuarterly(
  ingresosImponibles: number,
  gastosDeduciblesInfo: { costsBaseCash: number; ivaSoportadoDeducible: number; social: number },
  ivaRepercutido: number,
  retencionesSufridas: number,
  pagos130Anual: number,
  dist: QuarterDistribution
): { qrows: QuarterRow[], total: any } {
  const qrows: QuarterRow[] = [];
  const quarters = [1,2,3,4] as (1|2|3|4)[];

  for (let i=0; i<4; i++) {
    const q = quarters[i];

    const ingQ = ingresosImponibles * dist.income[i];
    const ivaRepQ = ivaRepercutido * dist.income[i];

    const costsCashQ = gastosDeduciblesInfo.costsBaseCash * dist.costs[i];
    const ivaSopQ   = gastosDeduciblesInfo.ivaSoportadoDeducible * dist.costs[i];

    const socialQ = gastosDeduciblesInfo.social / 4;

    const retQ   = retencionesSufridas * dist.income[i];
    const p130Q  = pagos130Anual * dist.income[i];
    const irpfAnticiposQ = retQ + p130Q;

    const ivaNetQ = ivaRepQ - ivaSopQ;

    const netoCajaQ = (ingQ - retQ) - (costsCashQ + socialQ + p130Q);

    qrows.push({
      quarter: q,
      ingresosImponibles: ingQ,
      gastosDeducibles: costsCashQ,
      ivaRepercutido: ivaRepQ,
      ivaSoportadoDeducible: ivaSopQ,
      retencionesSufridas: retQ,
      pagos130: p130Q,
      irpfAnticipos: irpfAnticiposQ,
      liquidacionIVA: ivaNetQ,
      netoCaja: netoCajaQ
    });
  }

  const total = qrows.reduce((acc, r) => ({
    ingresosImponibles: acc.ingresosImponibles + r.ingresosImponibles,
    gastosDeducibles: acc.gastosDeducibles + r.gastosDeducibles,
    ivaRepercutido: acc.ivaRepercutido + r.ivaRepercutido,
    ivaSoportadoDeducible: acc.ivaSoportadoDeducible + r.ivaSoportadoDeducible,
    retencionesSufridas: acc.retencionesSufridas + r.retencionesSufridas,
    pagos130: acc.pagos130 + r.pagos130,
    irpfAnticipos: acc.irpfAnticipos + r.irpfAnticipos,
    liquidacionIVA: acc.liquidacionIVA + r.liquidacionIVA,
    netoCaja: acc.netoCaja + r.netoCaja
  }), { ingresosImponibles:0, gastosDeducibles:0, ivaRepercutido:0, ivaSoportadoDeducible:0, retencionesSufridas:0, pagos130:0, irpfAnticipos:0, liquidacionIVA:0, netoCaja:0 });

  return { qrows, total };
}

function retentionRateOf(activity: ActivityLine, rules: any, sevenPctFlag: boolean) {
  const aType = activity.retenType;
  if (aType === "profesional_7") return sevenPctFlag ? rules.settings.retentionProfessional7 : rules.settings.retentionProfessional15;
  if (aType === "profesional_15") return rules.settings.retentionProfessional15;
  if (aType === "rent_19") return rules.settings.retentionRent19;
  if (aType === "custom") return (activity.customRetentionPct ?? 0) / 100;
  return 0;
}

function vatRateFromType(vatType: string, vatRates: any, customPct?: number) {
  if (vatType === "custom") {
    const pct = typeof customPct === "number" ? customPct : 0;
    return Math.max(0, pct) / 100;
  }
  if (vatType === "general_21") return vatRates.general_21;
  if (vatType === "reduced_10") return vatRates.reduced_10;
  return vatRates.superreduced_4;
}

function computeVAT(activities: ActivityLine[], costsDeductibleVAT: number, prorrataPct: number, vatRates: any): VATDetail {
  const ivaRepercutido = activities.reduce((acc, a) => {
    if (a.exempt || a.vatType === "exempt" || a.vatType === "reverse_charge_eu") return acc;
    const rate = vatRateFromType(a.vatType, vatRates, a.customVatPct);
    return acc + toAnnual(a.baseAmount, a.frequency) * rate;
  }, 0);

  const ivaSoportadoDeducible = costsDeductibleVAT * prorrataPct;
  const liquidacionAnual = ivaRepercutido - ivaSoportadoDeducible;
  return { ivaRepercutido, ivaSoportadoDeducible, prorrataPct, liquidacionAnual };
}

function computePayments(
  rendimientoNeto: number,
  socialSecurityAnnual: number,
  rules: RulesPack,
  activities: ActivityLine[]
): PaymentsDetail {
  const ratioRet = ratioIngresosConRetencion(activities);
  let pagosFraccionados130 = 0;
  if (ratioRet < 0.70) {
    pagosFraccionados130 = rendimientoNeto * rules.settings.pagosFraccionadosPctEDSimplificada * (1 - ratioRet);
  }
  return {
    pagosFraccionados130,
    retencionesPracticadas: 0,
    retencionesSufridas: 0,
    cuotaRETA: socialSecurityAnnual
  };
}

function minimoPorHijos(hijosCount: number, hijosMenores3: number): number {
  const h = Math.max(0, hijosCount | 0);
  const hMenores3 = Math.min(Math.max(0, hijosMenores3 | 0), h);

  // Mínimo base por descendientes (régimen general)
  let minimoBase = 0;
  if (h >= 1) minimoBase += 2400;
  if (h >= 2) minimoBase += 2700;
  if (h >= 3) minimoBase += 4000;
  if (h >= 4) minimoBase += 4500 * (h - 3);

  // Plus por menores de 3 años: +2.800€ por cada uno
  const plusMenores3 = 2800 * hMenores3;

  return minimoBase + plusMenores3;
}

function minimoPorAscendientes(ascCount: number): number {
  return Math.max(0, ascCount|0) * 1150;
}

function minimoPorDependDiscapacidad(depDiscCount: number): number {
  return Math.max(0, depDiscCount|0) * 3000;
}

function deduccionFamiliaNumerosa(flag: "none" | "general" | "especial"): number {
  if (flag === "general") return 1200;
  if (flag === "especial") return 2400;
  return 0;
}

function sugerirCuotaRETA(rendimientoAnual: number, year: number): number {
  return suggestRetaAnnualFromIncome(year, rendimientoAnual);
}

// Calcula deducciones adicionales sobre la cuota de IRPF
// (aplicadas después del cálculo base con tramos progresivos)
function computeExtraDeductions(region: RegionKey, rules: RulesPack, personal: CalculatorInputs["personal"]) {
  const arr: { label: string; amount: number }[] = [];

  // Deducción autonómica por alquiler de vivienda habitual
  // Se aplica según la configuración de cada comunidad autónoma
  // NOTA: No todas las CCAA tienen esta deducción configurada. Ampliar en rules.json según necesidad.
  const reg = rules.regions[region];
  if (personal.rentHouseholder && reg?.deductions?.rentHouseholder) {
    const rentDeduction = reg.deductions.rentHouseholder;
    // El porcentaje se aplica sobre el importe pagado (simplificado: usamos el cap como base)
    const baseAlquiler = rentDeduction.cap;
    const deduccionAlquiler = Math.min(baseAlquiler * rentDeduction.pct, rentDeduction.cap);
    if (deduccionAlquiler > 0) {
      arr.push({ label: "Deducción autonómica alquiler", amount: deduccionAlquiler });
    }
  }

  // Deducción por donativos (régimen general)
  // Primeros 250€: 80%, resto: 40% (escala estatal 2025, simplificada)
  const donations = personal.donationsAnnual || 0;
  if (donations > 0) {
    const primeros250 = Math.min(donations, 250) * 0.80;
    const resto = Math.max(0, donations - 250) * 0.40;
    const deduccionDonativos = primeros250 + resto;
    arr.push({ label: "Donativos (régimen general)", amount: deduccionDonativos });
  }

  return arr;
}

export function solveGrossFromNet(
  targetNetMonthly: number,
  inputs: CalculatorInputs,
  rules: RulesPack
): number {
  const epsilon = 1;
  const maxIterations = 50;
  let min = 0;
  let max = 1000000;
  let iteration = 0;

  while (iteration < maxIterations && (max - min) > epsilon) {
    const mid = (min + max) / 2;

    const testInputs = JSON.parse(JSON.stringify(inputs)) as CalculatorInputs;

    if (testInputs.activities.length === 0) {
      testInputs.activities = [{
        concept: "Ingresos",
        baseAmount: mid,
        vatType: "general_21",
        retenType: "profesional_15"
      }];
    } else {
      testInputs.activities[0].baseAmount = mid;
    }

    try {
      const result = calculateAll(testInputs, rules);
      const netMonthly = (result.netoFinalTrasRenta ?? result.netoFiscalAnual ?? 0) / 12;

      if (Math.abs(netMonthly - targetNetMonthly) <= epsilon) {
        return mid;
      }

      if (netMonthly < targetNetMonthly) {
        min = mid;
      } else {
        max = mid;
      }
    } catch (error) {
      min = mid;
    }

    iteration++;
  }

  return (min + max) / 2;
}

export function calculateAll(inputs: CalculatorInputs, rules: RulesPack): CalculationOutput {
  validateInputs(inputs);

  const ingresosImponibles = sum(inputs.activities.map(a => toAnnual(a.baseAmount, a.frequency)));

  const totalExentos = sum(inputs.activities.filter(a => a.exempt || a.vatType === "exempt").map(a => toAnnual(a.baseAmount, a.frequency)));
  const prorrataPct = ingresosImponibles > 0 ? clamp01((ingresosImponibles - totalExentos) / ingresosImponibles) : 1;

  const costsDeducibleBase = sum(inputs.costs.map(c => {
    const effectivePct = c.deductiblePct === -1 ? (c.customDeductible ?? 100) : (c.deductiblePct ?? 100);
    return toAnnual(c.baseAmount, c.frequency) * effectivePct / 100;
  }));
  const costsVAT = sum(
    inputs.costs.map(c => {
      if (!c.hasVAT) return 0;
      const rate = c.vatType ? vatRateFromType(c.vatType, rules.vat, c.customVatPct) : 0;
      const effectivePct = c.deductiblePct === -1 ? (c.customDeductible ?? 100) : (c.deductiblePct ?? 100);
      return toAnnual(c.baseAmount, c.frequency) * rate * effectivePct / 100;
    })
  );

  const housingAffectation = clamp01(inputs.housingAffectationPct / 100);
  const housingExtra = (inputs.utilitiesAnnual || 0) * housingAffectation * 0.30;

  // Vehículo: solo se considera 100% deducible si está afecto de forma exclusiva (criterio AEAT).
  const vehicleAffectation = inputs.vehicleAffectationPct >= 100 ? 1 : 0;
  const vehicleExtra = vehicleAffectation ? (inputs.vehicleCostsAnnual || 0) : 0;

  const amortAnnual = sum(inputs.amortizations.map((a: AmortizationLine) => {
    if (inputs.year < a.yearStart) return 0;
    return a.acquisitionValue * (a.annualPct / 100);
  }));

  const healthDeductible = Math.min(
    inputs.personal.healthInsuranceAnnual || 0,
    rules.settings.healthInsuranceCapPerPerson * Math.max(1, inputs.personal.healthInsuranceCoveredPersons)
  );

  const gastosDeducibles = costsDeducibleBase + housingExtra + vehicleExtra + amortAnnual + healthDeductible;

  const rendimientoPrevio = ingresosImponibles - gastosDeducibles;

  const suggestedRETAAnnual = sugerirCuotaRETA(rendimientoPrevio, inputs.year);

  let social = suggestedRETAAnnual;
  if (inputs.isTarifaPlana) {
    const tarifaPlana = getTarifaPlanaDetails(inputs.year);
    social = tarifaPlana.totalAnnual;
    inputs.socialSecurityAnnual = tarifaPlana.totalAnnual;
  } else if (!inputs.autoRETA) {
    social = inputs.socialSecurityAnnual && inputs.socialSecurityAnnual > 0
      ? inputs.socialSecurityAnnual
      : suggestedRETAAnnual;
  } else {
    inputs.socialSecurityAnnual = suggestedRETAAnnual;
  }
  const retaActiveMode = inputs.profile?.retaActiveMode ?? "full_year";
  const retaActiveDaysRaw = inputs.profile?.retaActiveDays ?? 0;
  const retaFactor = retaActiveMode === "days" ? clamp01(retaActiveDaysRaw / 365) : 1;
  social = Math.round(social * retaFactor * 100) / 100;
  const rendimientoNeto = rendimientoPrevio - social;

  // ========== C\u00c1LCULO DE IRPF ==========

  const p = inputs.personal || ({} as any);
  const hijosCount = Number(p.hijosCount ?? 0);
  const hijosMenores3 = Number(p.hijosMenores3Count ?? 0);
  const ascendientesCount = Number(p.ascendientesCount ?? 0);
  const depDiscCount = Number(p.dependientesDiscapacidadCount ?? 0);
  const famNum = (p.familiaNumerosa ?? "none") as "none"|"general"|"especial";

  // M\u00cdNIMO PERSONAL Y FAMILIAR
  // Mínimo personal fijo (régimen general, sin considerar edad)
  const minimoPersonal = 5550;

  // El m\u00ednimo familiar suma los m\u00ednimos por hijos, ascendientes y dependientes con discapacidad
  // Estos m\u00ednimos reducen la base liquidable del IRPF antes de aplicar los tramos
  const minimoHijos = minimoPorHijos(hijosCount, hijosMenores3);
  const minimoAsc = minimoPorAscendientes(ascendientesCount);
  const minimoDisc = minimoPorDependDiscapacidad(depDiscCount);
  const minimoFamiliar = minimoHijos + minimoAsc + minimoDisc;
  const minimoTotal = minimoPersonal + minimoFamiliar;

  // REDUCCI\u00d3N POR GASTOS DE DIF\u00cdCIL JUSTIFICACI\u00d3N (DFJ)
  // Si se marca el checkbox, aplica 5% del rendimiento neto (m\u00e1x. 2.000\u20ac)
  const aplicaDFJ = !!inputs.aplicaDFJ;
  const reduccionDFJ = aplicaDFJ ? Math.min(2000, Math.max(0, rendimientoNeto * 0.05)) : 0;

  // REDUCCI\u00d3N GENERAL POR RENDIMIENTOS DEL TRABAJO (aplicable a aut\u00f3nomos)
  function reduccionGeneralRendimientos(rn: number): number {
    if (rn <= 14450) return 3700;
    if (rn <= 19999) return 3700 - (1156 * (rn - 14450) / 5550);
    return 0;
  }
  const reduccionRendimiento = inputs.isLowIncomeDependent ? reduccionGeneralRendimientos(rendimientoNeto) : 0;

  // BASE IMPONIBLE DEL IRPF
  // Se resta: reducci\u00f3n general, aportaciones a planes de pensiones y DFJ
  // Las aportaciones a planes de pensiones reducen la base imponible general (l\u00edmite 1.500\u20ac en 2025 para la mayor\u00eda)
  const aportacionesPensiones = Math.min(inputs.personal.pensionContribAnnual || 0, 1500); // Simplificado: l\u00edmite gen\u00e9rico
  const baseIRPFSinMinimos = Math.max(0, rendimientoNeto - reduccionRendimiento - aportacionesPensiones - reduccionDFJ);
  const baseImponibleIRPF = baseIRPFSinMinimos;

  // RESOLUCIÓN DE DATOS DE LA REGIÓN DESDE rules.json
  // Manejar referencias tipo "$ref:general.irpfTramosEstatales"
function resolveRegionRules(regionKey: RegionKey, rules: RulesPack): RegionRules {
    const regionData = rules.regions[regionKey] || rules.regions.general;

    let irpfTramosEstatales = regionData.irpfTramosEstatales;
    let irpfTramosAutonomicos = regionData.irpfTramosAutonomicos;

    if (typeof irpfTramosEstatales === 'string' && irpfTramosEstatales.startsWith('$ref:')) {
      irpfTramosEstatales = rules.regions.general.irpfTramosEstatales;
    }
    if (typeof irpfTramosAutonomicos === 'string' && irpfTramosAutonomicos.startsWith('$ref:')) {
      irpfTramosAutonomicos = rules.regions.general.irpfTramosAutonomicos;
    }

    return {
      irpfTramosEstatales: irpfTramosEstatales as { upTo: number; rate: number }[],
      irpfTramosAutonomicos: irpfTramosAutonomicos as { upTo: number; rate: number }[]
    };
  }

  const regionRules = resolveRegionRules(inputs.region, rules);

  // CÁLCULO DEL IRPF CON TRAMOS PROGRESIVOS
  // El mínimo total (personal + familiar) reduce la base liquidable
  const irpfRes = calcIRPF(baseImponibleIRPF, regionRules, minimoTotal);

  // DEDUCCIONES SOBRE LA CUOTA (aplicadas despu\u00e9s del c\u00e1lculo con tramos)
  const extraDeductions = computeExtraDeductions(inputs.region, rules, inputs.personal);

  // Aplicar deducciones por donativos y alquiler
  let irpfTotalConDeducciones = irpfRes.cuotaTotal;
  for (const d of extraDeductions) {
    irpfTotalConDeducciones = Math.max(0, irpfTotalConDeducciones - d.amount);
  }

  // DEDUCCI\u00d3N POR FAMILIA NUMEROSA
  // 1.200\u20ac (general) o 2.400\u20ac (especial) sobre la cuota
  const dedFamNum = deduccionFamiliaNumerosa(famNum);
  if (dedFamNum > 0) {
    extraDeductions.push({ label: `Familia numerosa (${famNum})`, amount: dedFamNum });
  }
  irpfTotalConDeducciones = Math.max(0, irpfTotalConDeducciones - dedFamNum);

  // DEDUCCIONES AUTONÓMICAS MANUALES
  const deduccionesAutoManuales = inputs.deduccionesAutonomicasManuales ?? 0;
  let cuotaAutonomicaFinal = irpfRes.cuotaAutonomica;
  if (deduccionesAutoManuales > 0) {
    cuotaAutonomicaFinal = Math.max(0, cuotaAutonomicaFinal - deduccionesAutoManuales);
    extraDeductions.push({ label: "Deducciones autonómicas", amount: deduccionesAutoManuales });
    irpfTotalConDeducciones = Math.max(0, irpfTotalConDeducciones - deduccionesAutoManuales);
  }

  const irpf: IRPFDetail = {
    baseGeneral: baseImponibleIRPF,
    cuotaEstatal: irpfRes.cuotaEstatal,
    cuotaAutonomica: cuotaAutonomicaFinal,
    cuotaTotal: irpfTotalConDeducciones,
    deductions: extraDeductions
  };

  const iva = computeVAT(inputs.activities, costsVAT, prorrataPct, rules.vat);

  const pagos = computePayments(rendimientoNeto, social, rules, inputs.activities);

  const retencionesSufridas = inputs.activities.reduce((acc, a) => {
    const rate = retentionRateOf(a, rules, inputs.retenApplies7Pct);
    return acc + a.baseAmount * rate;
  }, 0);

  pagos.retencionesSufridas = retencionesSufridas;

  const netoFiscalAnual = rendimientoNeto - irpf.cuotaTotal;

  const costsBaseCash = totalCostsBaseCash(
    costsDeducibleBase,
    housingExtra,
    vehicleExtra,
    inputs.personal.healthInsuranceAnnual || 0
  );

  const anticiposIRPFAnual = retencionesSufridas + pagos.pagosFraccionados130;

  const cajaAnualReal =
    ingresosImponibles
    - costsBaseCash
    - social
    - anticiposIRPFAnual;

  const netoCajaMensualMedio = cajaAnualReal / 12;

  const ivaNetAnual = vatNet(iva.ivaRepercutido, iva.ivaSoportadoDeducible);

  const timeline: TimelineCashMonth[] = Array.from({ length: 12 }, (_, m) => ({
    month: m + 1,
    inflows: (ingresosImponibles - retencionesSufridas) / 12,
    outflows: (costsBaseCash + social + pagos.pagosFraccionados130 + ivaNetAnual) / 12,
    net: 0
  }));
  for (const t of timeline) t.net = t.inflows - t.outflows;

  const qdist = normQuarterDist(inputs.quarterDist);
  const quarterly = buildQuarterly(
    ingresosImponibles,
    { costsBaseCash, ivaSoportadoDeducible: iva.ivaSoportadoDeducible, social },
    iva.ivaRepercutido,
    retencionesSufridas,
    pagos.pagosFraccionados130,
    qdist
  );

  const brutoFacturado = ingresosImponibles;
  const costesCash = costsBaseCash;
  const cuotaRETAAnual = social;
  const irpfTotal = Number(irpf?.cuotaTotal ?? 0);
  const anticiposIRPF_retenciones = Number(retencionesSufridas ?? 0);
  const anticiposIRPF_mod130 = Number(pagos?.pagosFraccionados130 ?? 0);
  const anticiposIRPF = anticiposIRPF_retenciones + anticiposIRPF_mod130;

  const irpfRegularizacion = irpfTotal - anticiposIRPF;

  const cajaPreviaRenta = brutoFacturado - costesCash - cuotaRETAAnual - anticiposIRPF;

  const netoFinalTrasRenta = cajaPreviaRenta - irpfRegularizacion;

  const irpfPctSobreBaseIRPF = baseImponibleIRPF > 0 ? (irpfTotal / baseImponibleIRPF) * 100 : 0;
  const irpfPctSobreBruto = brutoFacturado > 0 ? (irpfTotal / brutoFacturado) * 100 : 0;

  const irpfPendiente = Math.max(0, irpfRegularizacion);
  const irpfDevolucion = Math.max(0, -irpfRegularizacion);
  const seguroMedicoAplicado = healthDeductible;

  return {
    rendimientoNeto,
    baseIRPF: baseImponibleIRPF,
    irpf,
    iva,
    pagos,
    netoFiscalAnual,
    netoCajaMensualMedio,
    timeline,
    quarterly,
    minimoPersonalFamiliar: minimoTotal,
    minimoPersonal,
    minimoFamiliar,
    minimoHijos,
    hijosCount,
    hijosMenores3Count: hijosMenores3,
    brutoFacturado,
    costesCash,
    cuotaRETAAnual,
    irpfTotal,
    anticiposIRPF,
    anticiposIRPF_retenciones,
    anticiposIRPF_mod130,
    irpfRegularizacion,
    cajaPreviaRenta,
    irpfPendiente,
    irpfDevolucion,
    netoFinalTrasRenta,
    seguroMedicoAplicado,
    utilitiesDeduction: housingExtra,
    housingAffectationApplied: housingAffectation,
    vehicleDeduction: vehicleExtra,
    vehicleAffectationApplied: vehicleAffectation,
    irpfPctSobreBaseIRPF,
    irpfPctSobreBruto,
    suggestedRETAAnnual,
    retaMode: inputs.autoRETA ? "auto" : "manual",
    reduccionDFJ,
    baseImponibleIRPF,
    irpfEstatal: irpfRes.cuotaEstatal,
    irpfAutonomica: irpfRes.cuotaAutonomica,
    irpfCuotaMinimo: irpfRes.cuotaMinimo,
    minimoPersonalUsado: minimoTotal
  };
}
